import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import TopThreeTile from './components/top-three-tile/top-three-tile';
import { PLP_PAGE_TYPE } from '../../product-list-page-constants';

import {
  STAGGERED_TOP_THREE,
  STANDARD_TOP_THREE
} from './graphql/top-three-queries';

import {
  useDataLayer,
  useFitment,
  useModal,
  useMyStore,
  useProductEntryDataLayerValues,
  useProductQuantity,
  useTreadwellPersonas,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import {
  getVehicleInput,
  parseTireSize
} from '../../../../shared/utils/fitment/fitment';
import {
  getPriceInfo,
  getProductImage
} from '../../../../shared/utils/product/product';
import { getTreadwellGQLInput } from '../../../../shared/utils/treadwell/treadwell';

import { FuelTypeEnum } from '../../../../shared/types/graphql-types';
import {
  PRODUCT_LIST_TOP_3,
  PRODUCT_LIST_TOP_3_BRANDS,
  PRODUCT_LIST_TOP_3_DISPLAYED,
  PRODUCT_LIST_TOP_3_IDS,
  PRODUCT_LIST_TOP_3_MET,
  PRODUCT_LIST_TOP_3_NAMES,
  PRODUCT_LIST_TOP_3_POSITION,
  PRODUCT_LIST_TOP_3_TYPES,
  PRODUCT_POSITION
} from '../../../../shared/constants/data-layer';
import { MODAL_ROUTE_PATTERN } from '../../../../shared/constants/modals';
import { PRODUCT_IMAGE_FORMATS } from '../../../../shared/constants/product-images';
import { STAGGERED_SEARCH_TYPES } from '../../../../shared/constants/staggered-products';
import {
  TREADWELL_DEFAULT_PERSONA_NAME,
  treadwellDefaults
} from '../../../../shared/constants/treadwell';

import './top-three.scss';

const TOP_3_TYPES = {
  BEST_SELLER: 'BS',
  ORIGINAL_EQUIPMENT: 'OE',
  RECOMMENDED: 'REC'
};
const TOP_3_POSITIONS = ['101', '102', '103'];

function TopThree(props) {
  const { staggeredSearchType } = props;
  const { selectedFitment } = useFitment();
  const { hash } = useLocation();
  const { isOpen } = useModal();
  const [queryVariables, setQueryVariables] = useState();

  const { myStore } = useMyStore();
  const { defaultQuantity: quantity } = useProductQuantity();

  const { treadwellPreferences } = useTreadwellPreferences();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { currentPageType } = useDataLayer();
  const { getTreadwellPersonaData } = useTreadwellPersonas();

  const storeCode = myStore?.code;

  const vehicleInfo = useMemo(() => {
    if (selectedFitment) {
      return getVehicleInput(selectedFitment);
    }
  }, [selectedFitment]);

  useEffect(() => {
    async function setupQueryVariables() {
      if (isOpen || MODAL_ROUTE_PATTERN.test(hash)) {
        return;
      }

      if (!storeCode || !vehicleInfo) {
        setQueryVariables(null);
        return;
      }

      let treadwellRequest = getTreadwellGQLInput(
        selectedFitment,
        treadwellPreferences
      );

      if (!treadwellRequest) {
        const treadwellDefaultsToUse = {
          ...treadwellDefaults
        };

        try {
          const { personasList } = await getTreadwellPersonaData(
            selectedFitment
          );
          const defaultPersona = personasList?.find(
            persona => persona.name === TREADWELL_DEFAULT_PERSONA_NAME
          );

          if (defaultPersona) {
            treadwellDefaultsToUse.drivingStyle =
              TREADWELL_DEFAULT_PERSONA_NAME;
            defaultPersona.tooltips?.forEach((tooltip, i) => {
              treadwellDefaultsToUse[`priority${i + 1}`] = tooltip?.id;
            });
          }
        } catch (e) {
          console.error(
            'Unable to fetch persona defaults for Suburb & City',
            e
          );
        }

        if (selectedFitment) {
          treadwellDefaultsToUse.fuelType =
            selectedFitment?.fuelType || FuelTypeEnum.Ice;
        }

        treadwellRequest = {
          ...treadwellDefaultsToUse,
          isOESize: selectedFitment?.isOE,
          quantity,
          zipCode: myStore?.address?.postalCode
        };
      }

      const frontSize = parseTireSize(selectedFitment?.frontTireSize);
      const searchInput = {
        front: frontSize,
        treadwell: treadwellRequest
      };

      if (selectedFitment?.isStaggered && staggeredSearchType) {
        searchInput.rear = parseTireSize(selectedFitment?.rearTireSize);
        searchInput.staggeredType = staggeredSearchType;
      }

      setQueryVariables({
        searchInput,
        storeCode,
        vehicleInfo
      });
    }
    setupQueryVariables();
  }, [
    getTreadwellPersonaData,
    hash,
    storeCode,
    selectedFitment,
    staggeredSearchType,
    treadwellPreferences,
    quantity,
    myStore,
    vehicleInfo,
    isOpen
  ]);

  const { data: standardData } = useQuery(STANDARD_TOP_THREE, {
    skip: !queryVariables || selectedFitment?.isStaggered,
    variables: queryVariables
  });

  const { data: staggeredData } = useQuery(STAGGERED_TOP_THREE, {
    skip: !queryVariables || !selectedFitment?.isStaggered,
    variables: queryVariables
  });

  const tiles =
    standardData?.fitmentSearch?.standardTiles?.productTiles ||
    staggeredData?.fitmentSearch?.staggeredTiles?.productTiles;
  const shouldDisplay = Array.isArray(tiles) && tiles.length === 3;

  useEffect(() => {
    if (shouldDisplay && selectedFitment && currentPageType === PLP_PAGE_TYPE) {
      const top3 = [];
      const top3Brands = [];
      const top3Ids = [];
      const top3Names = [];
      const top3Types = [];

      for (let t = 0; t < tiles.length; t++) {
        const tile = tiles[t];
        const { productData, tile: tileType } = tile || {};
        const { code, brand, name } = productData || {};
        const formattedTileType = TOP_3_TYPES[tileType];

        top3.push(brand, null, code, formattedTileType);
        top3Brands.push(brand);
        top3Ids.push(code || null);
        top3Names.push(name || null);
        top3Types.push(formattedTileType || null);
      }

      trackAction('product_list_top3_view', {
        [PRODUCT_LIST_TOP_3]: top3,
        [PRODUCT_LIST_TOP_3_BRANDS]: top3Brands,
        [PRODUCT_LIST_TOP_3_DISPLAYED]: shouldDisplay,
        [PRODUCT_LIST_TOP_3_IDS]: top3Ids,
        [PRODUCT_LIST_TOP_3_MET]: 'Success',
        [PRODUCT_LIST_TOP_3_NAMES]: top3Names,
        [PRODUCT_LIST_TOP_3_POSITION]: TOP_3_POSITIONS,
        [PRODUCT_LIST_TOP_3_TYPES]: top3Types
      });
    }
  }, [shouldDisplay, tiles, currentPageType, selectedFitment]);

  if (!shouldDisplay) {
    return null;
  }

  return (
    <div styleName="container">
      {tiles
        .sort((t1, t2) => (t1?.sequenceNumber || 0) - (t2?.sequenceNumber || 0))
        .reduce((acc, t) => {
          if (typeof t.productData !== 'object' || t.productData === null) {
            return acc;
          }

          const {
            brand,
            code,
            mapRuleSatisfied,
            mapDisplayRule,
            name,
            rearProduct,
            recommendationData,
            unitName,
            url
          } = t.productData || {};
          const { rank } = recommendationData || {};
          const priceInfo = getPriceInfo(t.productData);
          const rearPriceInfo = getPriceInfo(rearProduct);
          const pos = TOP_3_POSITIONS[acc.length];
          const priceSubtext =
            mapRuleSatisfied &&
            mapDisplayRule &&
            mapDisplayRule.messageDisplay &&
            mapDisplayRule.mapPriceDisplay
              ? t.productData.mapDisplayRule.message
              : '';

          acc.push(
            <TopThreeTile
              brand={brand}
              detailsLink={url}
              imageUrl={
                getProductImage(t.productData, {
                  format: PRODUCT_IMAGE_FORMATS.LIST,
                  shouldFallBackToFirst: true
                })?.url
              }
              isTreadwell={rank}
              key={`${code}-${t.tile}`}
              name={name}
              onViewDetailsClick={e =>
                trackAction(e, {
                  ...getProductEntryDataLayerValues(t.productData),
                  [PRODUCT_POSITION]: rearProduct ? [pos, pos] : [pos]
                })
              }
              priceInfo={priceInfo}
              priceSubtext={priceSubtext}
              rank={rank}
              rearCode={rearProduct?.code}
              rearPriceInfo={rearPriceInfo}
              type={t.tile}
              unitName={unitName}
            />
          );

          return acc;
        }, [])}
    </div>
  );
}

TopThree.propTypes = {
  staggeredSearchType: PropTypes.oneOf([
    STAGGERED_SEARCH_TYPES.SET,
    STAGGERED_SEARCH_TYPES.FRONT,
    STAGGERED_SEARCH_TYPES.REAR
  ])
};

export default TopThree;
